import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Spin, Alert, Tag, Typography, Divider, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { sheepApi } from '../api';
import { FullPedigree, AncestorNode, DescendantNode } from '../types';

const { Text } = Typography;

const COLORS = {
  male: { border: '#1677ff', bg: '#e6f4ff' },
  female: { border: '#eb2f96', bg: '#fff0f6' },
  center: { border: '#764ba2', bg: '#f9f0ff' },
};

function genderIcon(g: string) { return g === 'male' ? '♂' : '♀'; }

interface MiniCardProps {
  id: number;
  name: string;
  tagNumber: string;
  gender: string;
  birthDate?: string;
  breed?: string;
  isCenter?: boolean;
  onClick?: () => void;
}

function MiniCard({ id, name, tagNumber, gender, birthDate, breed, isCenter, onClick }: MiniCardProps) {
  const col = isCenter ? COLORS.center : (gender === 'male' ? COLORS.male : COLORS.female);
  return (
    <div
      onClick={onClick}
      style={{
        border: `2px solid ${col.border}`,
        background: col.bg,
        borderRadius: 10,
        padding: isCenter ? '10px 16px' : '7px 12px',
        cursor: onClick ? 'pointer' : 'default',
        minWidth: isCenter ? 190 : 130,
        maxWidth: isCenter ? 220 : 170,
        textAlign: 'center',
        transition: 'box-shadow 0.15s',
        boxShadow: isCenter ? '0 2px 12px rgba(118,75,162,0.2)' : undefined,
        flexShrink: 0,
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.18)'; }}
      onMouseLeave={(e) => { if (onClick) e.currentTarget.style.boxShadow = isCenter ? '0 2px 12px rgba(118,75,162,0.2)' : ''; }}
    >
      <div style={{ fontWeight: isCenter ? 700 : 600, fontSize: isCenter ? 15 : 13 }}>
        {genderIcon(gender)} {name}
      </div>
      <div style={{ fontSize: 11, color: '#666' }}>{tagNumber}</div>
      {breed && <Tag style={{ fontSize: 10, marginTop: 2 }}>{breed}</Tag>}
      {birthDate && <div style={{ fontSize: 11, color: '#999' }}>{new Date(birthDate).getFullYear()}</div>}
    </div>
  );
}

// Vertical connector
function V({ h = 20 }: { h?: number }) {
  return <div style={{ width: 2, height: h, background: '#d9d9d9', margin: '0 auto' }} />;
}

// Horizontal connector spanning full width between two children
function HBridge() {
  return <div style={{ height: 2, background: '#d9d9d9', flex: 1, minWidth: 12 }} />;
}

// ─── ANCESTOR TREE (renders top-down, root at bottom) ───────────────────────
interface AncestorBranchProps {
  node: AncestorNode | null | undefined;
  navigate: (id: number) => void;
}

function AncestorBranch({ node, navigate: nav }: AncestorBranchProps) {
  if (!node) return null;

  const hasParents = node.father || node.mother;
  const siblings = node.siblings ?? [];

  // The main node + its siblings in a horizontal group
  const NodeWithSiblings = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
      <MiniCard {...node} onClick={() => nav(node.id)} />
      {siblings.length > 0 && (
        <>
          <div style={{ width: 1, height: 40, background: '#d9d9d9', margin: '0 2px' }} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {siblings.map((s) => (
              <MiniCard key={s.id} {...s} onClick={() => nav(s.id)} />
            ))}
          </div>
        </>
      )}
    </div>
  );

  if (!hasParents) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <NodeWithSiblings />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Parents row */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0 }}>
        {node.father && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <AncestorBranch node={node.father} navigate={nav} />
            <V />
          </div>
        )}
        {node.father && node.mother && (
          <div style={{ width: 32, height: 2, background: '#d9d9d9', alignSelf: 'flex-end', marginBottom: 20 }} />
        )}
        {node.mother && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <AncestorBranch node={node.mother} navigate={nav} />
            <V />
          </div>
        )}
      </div>
      <NodeWithSiblings />
    </div>
  );
}

// ─── DESCENDANT TREE (renders top-down) ────────────────────────────────────
interface DescendantBranchProps {
  nodes: DescendantNode[];
  navigate: (id: number) => void;
}

function DescendantBranch({ nodes, navigate: nav }: DescendantBranchProps) {
  if (nodes.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
      {nodes.map((node, i) => (
        <React.Fragment key={node.id}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <V />
            <MiniCard {...node} onClick={() => nav(node.id)} />
            {node.children.length > 0 && <DescendantBranch nodes={node.children} navigate={nav} />}
          </div>
          {i < nodes.length - 1 && (
            <div style={{ width: 24, height: 2, background: '#d9d9d9', alignSelf: 'flex-start', marginTop: 20 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
export default function FullPedigreePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isPublic = searchParams.get('public') === '1';
  const navigate = useNavigate();

  const [data, setData] = useState<FullPedigree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fn = isPublic ? sheepApi.getPublicFullPedigree : sheepApi.getFullPedigree;
    fn(Number(id))
      .then((r) => setData(r.data))
      .catch(() => setError('Не удалось загрузить родословную'))
      .finally(() => setLoading(false));
  }, [id, isPublic]);

  const navToSheep = (sheepId: number) => {
    if (isPublic) window.location.href = `/sheep/${sheepId}`;
    else navigate(`/sheep/${sheepId}/detail`);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Spin size="large" />
    </div>
  );

  if (error || !data) return (
    <div style={{ maxWidth: 480, margin: '80px auto', padding: 16 }}>
      <Alert type="error" message={error} />
    </div>
  );

  const { sheep, ancestors, descendants, siblings } = data;
  const hasAncestors = ancestors?.father || ancestors?.mother;
  const hasDescendants = descendants.length > 0;
  const hasSiblings = siblings.length > 0;

  const background = isPublic
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : '#f5f5f5';

  return (
    <div style={{ minHeight: '100vh', background, padding: '24px 16px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => isPublic ? (window.location.href = `/sheep/${id}`) : navigate(`/sheep/${id}/detail`)}
            style={isPublic ? { background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff' } : {}}
          >
            Назад
          </Button>
          <Typography.Title level={4} style={{ margin: 0, color: isPublic ? '#fff' : undefined }}>
            🌳 Полная родословная — {sheep.name} ({sheep.tagNumber})
          </Typography.Title>
        </div>

        <Card style={{ borderRadius: 14, overflowX: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16, minWidth: 400 }}>

            {/* ── Ancestors ── */}
            {hasAncestors && (
              <>
                <div style={{ alignSelf: 'flex-start', marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>⬆ Предки</Text>
                </div>
                <div style={{ display: 'flex', gap: 0, alignItems: 'flex-end', justifyContent: 'center' }}>
                  {ancestors.father && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <AncestorBranch node={ancestors.father} navigate={navToSheep} />
                      <V />
                    </div>
                  )}
                  {ancestors.father && ancestors.mother && (
                    <div style={{ width: 40, height: 2, background: '#d9d9d9', alignSelf: 'flex-end', marginBottom: 20 }} />
                  )}
                  {ancestors.mother && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <AncestorBranch node={ancestors.mother} navigate={navToSheep} />
                      <V />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Current sheep ── */}
            <MiniCard {...sheep} isCenter />

            {/* ── Siblings ── */}
            {hasSiblings && (
              <div style={{ marginTop: 16, width: '100%' }}>
                <Divider style={{ margin: '12px 0 10px' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Братья и сёстры ({siblings.length})</Text>
                </Divider>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {siblings.map((s) => (
                    <MiniCard key={s.id} {...s} onClick={() => navToSheep(s.id)} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Descendants ── */}
            {hasDescendants && (
              <>
                <Divider style={{ margin: '16px 0 6px' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>⬇ Потомки</Text>
                </Divider>
                <DescendantBranch nodes={descendants} navigate={navToSheep} />
              </>
            )}

            {!hasAncestors && !hasDescendants && !hasSiblings && (
              <Text type="secondary" style={{ marginTop: 24 }}>Родственники не указаны</Text>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
